/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import InputField from '../InputField';
import Grid from '../Grid';
import GridItem from '../GridItem';
import getDeviceProperties from 'shared-utils/src/getDeviceProperties';
import createStyle from 'shared-utils/src/createStyle';
import styles, { mainCls } from './styles';
import makeClassName from 'shared-utils/src/makeClassName';


const useStyles = createStyle(styles);
/**
 * Input container that handles beahvior of external components
 */
export default function InputContainer({
  type,
  value,
  className,
  placeholder,
  placeholderBack,
  onChange,
  onFocus,
  onBlur,
  onEnd,
  iconClose,
  shortOnFocus,
  children,
  isValid,
  iconAfter,
  hasCancel,
  ...props
}) {
  const [query, setQuery] = useState(value);
  const [focused, setFocused] = useState(0);
  const [iconRight, setIconRight] = useState(iconAfter);
  const elRef = useRef(null);
  const cls = useStyles();

  const handleClickOutside = async (ev) => {
    const { target } = ev;
    const clickInside = elRef.current && elRef.current.contains(target);
    if (!clickInside) {
      onChange(query);
      setFocused();
      setTimeout(() => {
        setIconRight(iconAfter);
        onEnd();
      }, 250);

    }
  };

  useEffect(() => {
    const events = ['click'];
    getDeviceProperties().mobileOs === 'ios' && events.push('touchstart');
    focused &&
      events.forEach((ev) => document.addEventListener(ev, handleClickOutside));
    !focused && document.activeElement && document.activeElement.blur();
    return () =>
      events.forEach((ev) =>
        document.removeEventListener(ev, handleClickOutside)
      );
  }, [focused, query]); // https://github.com/facebook/react/issues/14066

  useEffect(() => {
    if (value !== query) {
      setQuery(value);
    }
  }, [value]);

  const handleOnFocus = (ev) => {
    setFocused(1);
    onFocus(ev);
    setIconRight(null);
  };
  const handleOnBlur = (ev) => {    
    handleOnEnd();
    onBlur(ev);
  };

  const handleOnEnd = () => {
    setFocused();
    onChange(query);
    setTimeout(() => {
      setIconRight(iconAfter);
      onEnd();
    }, 250);
  }

  const handleKeyDown = (ev) => {
    const key = ev.which;
    if ([27, 40, 38].indexOf(key) >= 0) ev.preventDefault();
    if (key === 13) {
      ev.preventDefault();
      return handleOnEnd();
    }
  };
  const inputValue = query || '';

  const plhBackRes =
    typeof placeholderBack === 'function'
      ? placeholderBack({
          focused,
          value: inputValue,
        })
      : placeholderBack;
  const inputProps = {
    value: inputValue,
    type,
    placeholder,
    placeholderBack: plhBackRes,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onChange: setQuery,
    onKeyDown: handleKeyDown,
    iconAfterAction: 'onfocusinput',
    isValid: isValid || (() => inputValue),
    iconClose,
    iconAfter: iconRight,
    autcomplete: 'none', // https://bugs.chromium.org/p/chromium/issues/detail?id=370363#c7
    ...(shortOnFocus && focused && { size: 'm' }),
    ...props,
  };
  const showCancel = !!(focused);
  const clsMain = makeClassName([cls[`${mainCls}`], className]);

  return (
    <div ref={elRef} className={clsMain}>
      <Grid align="center">
        <GridItem>
          <InputField {...inputProps} className="input-container__input" />
        </GridItem>
        {hasCancel && <GridItem
          className={`input-container__canc-btn is-clickable c-txt--f2 tp-s--s tp-w--m ${showCancel ? 'input-container__canc-btn--s' : ''}`}
          behavior="fixed"
          onMouseDown={() => handleOnEnd()}
        >
          Annulla
        </GridItem>}
      </Grid>
    </div>
  );
}

InputContainer.propTypes = {
  /**
   * Component class
   */
  className: PropTypes.string,

  /**
   * Type of the input
   */
  type: PropTypes.string,

  /**
   * Input size
   */
  size: PropTypes.string,

  /**
   * Input value
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  /**
   * Input placeholder
   */
  placeholder: PropTypes.string,

  /**
   * Background placeholder (eg. used like in autocompletes)
   */
  placeholderBack: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),

  /**
   * Check if cancel button has to be included
   */
  hasCancel: PropTypes.bool,

  /**
   * Callback when the input is changed
   */
  onChange: PropTypes.func,

  /**
   * Callback on input focus
   */
  onFocus: PropTypes.func,

  /**
   * Callback on input blur
   */
  onBlur: PropTypes.func,

  /**
   * Callback when the is not anymore focus
   */
  onEnd: PropTypes.func,

  /**
   * Check if the value is valid
   */
  isValid: PropTypes.func,

  /**
   * Icon for validation
   */
  iconValid: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),

  /**
   * Close icon
   */
  iconClose: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),

  /**
   * Icon after input
   */
  iconAfter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),

  /**
   * Set short on focus
   */
  shortOnFocus: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ])
};

InputContainer.defaultProps = {
  className: '',
  value: '',
  type: 'text',
  placeholder: '',
  placeholderBack: '',
  hasCancel: false,
  iconClose: false,
  onChange: () => { },
  onFocus: () => { },
  onBlur: () => { },
  onEnd: () => { },
  isValid: i => i,
  readOnly: false,
  iconValid: '',
  iconAfter: '',
  shortOnFocus: false,
};